// Libraries
import { useMutation, useQueryClient } from '@tanstack/react-query';

// Hooks and utilities
import { deleteEvent } from '@api/events/mutations';
import { eventsQueryKeys } from '@api/events';
import { ordersQueryKeys } from '@api/orders/queries';
import { schedulerEntriesQueryKeys } from '@api/scheduler';
import { getErrorDescription } from '@api/client';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from '../useTranslation';

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: deleteEvent,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: eventsQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: eventsQueryKeys.orderEvents(variables.orderId) });
      queryClient.invalidateQueries({ queryKey: schedulerEntriesQueryKeys.forOrder(variables.orderId) });
      // The server recomputes the order's denormalised totals on delete, so
      // refetch the order too or the totals summary keeps the deleted event.
      queryClient.invalidateQueries({ queryKey: ordersQueryKeys.detail(variables.orderId) });

      toast({
        description: t('events_section.destroy.success'),
      });
    },
    onError: (error) => {
      // Surfaces the server's 422 reason (e.g. event not editable in its
      // current state) when present; falls back to the generic copy.
      toast({
        description: getErrorDescription(error, t('events_section.destroy.error')),
        variant: 'destructive',
      });
    },
  });
}
